import { BasketApiClient, BasketApiError, IdleLifecycle, type BasketBootstrap } from "@basket/client";
import type { AgentId, AudioMode, ConnectionState, ObserverWorkActivity, RealtimeEndReason, SessionMode, ThreadRecord, ThreadSummary, UserSettings } from "@basket/protocol";
import { audioModeFor, nextAudioMode } from "@basket/protocol";
import type { BasketRealtimeClient as BasketRealtimeClientType } from "@basket/realtime";
import { BasketShell, SpecularBandBackground, type AgentAsset, type ChatMessage, type HistoryItem } from "@basket/ui";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { canPlayAmbientBgm, currentAmbientBgmPageState } from "./ambient-bgm.js";

const api = new BasketApiClient("");
const assets = {
  melo: { portrait: "/assets/agents/melo-portrait.png", sprite: "/assets/agents/melo-sprite.png" },
  peac: { portrait: "/assets/agents/peac-portrait.png", sprite: "/assets/agents/peac-sprite.png" },
  kiw: { portrait: "/assets/agents/kiw-portrait.png", sprite: "/assets/agents/kiw-sprite.png" },
  pomel: { portrait: "/assets/agents/pomel-portrait.png", sprite: "/assets/agents/pomel-sprite.png" },
} satisfies Record<AgentId, AgentAsset>;
const brand = { logo: "/assets/brand/basket-logo.png", mark: "/assets/brand/basket-mark.png", bgm: "/assets/brand/basket-bgm.mp3" };

function BasketPage({ children, bgmEnabled, onToggleBgm }: { children: ReactNode; bgmEnabled: boolean; onToggleBgm: () => void }) {
  return <div className="basket-web-surface">
    <SpecularBandBackground />
    <div className="basket-web-content">
      {children}
      <button type="button" className="basket-quick-bgm" aria-pressed={bgmEnabled} onClick={onToggleBgm}>{bgmEnabled ? "BGM OFF" : "BGM ON"}</button>
    </div>
  </div>;
}

function relativeTimestamp(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000));
  if (minutes < 1) return "たった今";
  if (minutes < 60) return `${minutes}分前`;
  if (minutes < 1_440) return `${Math.round(minutes / 60)}時間前`;
  return new Intl.DateTimeFormat("ja-JP", { month: "numeric", day: "numeric" }).format(new Date(value));
}
function historyItems(threads: ThreadSummary[]): HistoryItem[] { return threads.map((thread) => ({ id: thread.id, agent: thread.agent, label: thread.label, timestamp: relativeTimestamp(thread.updatedAt) })); }
function chatMessage(role: ChatMessage["role"], text: string, id: string = crypto.randomUUID()): ChatMessage { return { id, role, text }; }
function Login() {
  return <main className="basket-login">
    <img className="basket-login-logo" src={brand.logo} alt="Basket" />
    <a href={`/auth/slack?returnTo=${encodeURIComponent(window.location.href)}`}>
      <img className="basket-login-slack-icon" src="/assets/brand/slack.png" alt="" aria-hidden="true" />
      Slackでログイン
    </a>
  </main>;
}
function Loading() { return <main className="basket-login"><div className="basket-loading" /><span>Basketを準備しています…</span></main>; }

type LocalBgmPreference = { enabled: boolean; volume: number };
const defaultBgmPreference: LocalBgmPreference = { enabled: true, volume: .055 };
const pendingBgmPreferenceKey = "basket.bgm.pending";
function readLocalBgmPreference(): LocalBgmPreference {
  try {
    const value = JSON.parse(window.localStorage.getItem("basket.bgm") ?? "null") as Partial<LocalBgmPreference> | null;
    return value && typeof value.enabled === "boolean" && typeof value.volume === "number"
      ? { enabled: value.enabled, volume: Math.max(0, Math.min(.2, value.volume)) }
      : defaultBgmPreference;
  } catch { return defaultBgmPreference; }
}
function storeLocalBgmPreference(value: LocalBgmPreference) {
  try { window.localStorage.setItem("basket.bgm", JSON.stringify(value)); } catch { /* private mode may deny storage */ }
}
function readPendingBgmPreference(): boolean | undefined {
  try {
    const value = window.localStorage.getItem(pendingBgmPreferenceKey);
    return value === "true" ? true : value === "false" ? false : undefined;
  } catch { return undefined; }
}
function storePendingBgmPreference(value: boolean) {
  try { window.localStorage.setItem(pendingBgmPreferenceKey, String(value)); } catch { /* private mode may deny storage */ }
}
function clearPendingBgmPreference() {
  try { window.localStorage.removeItem(pendingBgmPreferenceKey); } catch { /* private mode may deny storage */ }
}
function updateBgmEnabled(settings: UserSettings, bgmEnabled: boolean) {
  const { version, timezone, profile, voiceByAgent, bgmVolume } = settings;
  return api.updateSettings({ version, timezone, profile, voiceByAgent, bgmEnabled, bgmVolume });
}
function connectionFailureMessage(error: unknown): string {
  if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
    return "マイクが許可されていません。Safariのサイト設定からマイクを許可してください。";
  }
  const detail = error instanceof Error ? error.message : String(error ?? "");
  if (/(?:status\s*429|no credits|quota|billing)/iu.test(detail)) {
    return "現在、音声サービスの利用枠が上限に達しています。管理者の復旧後にもう一度お試しください。";
  }
  return "通話に接続できませんでした。少し待ってからもう一度お試しください。";
}

function ExtensionPairing({ displayName }: { displayName: string }) {
  const [code, setCode] = useState<string>();
  const [busy, setBusy] = useState(false);
  const issue = async () => {
    setBusy(true);
    try {
      const response = await fetch("/api/extension/pairing-code", { method: "POST", credentials: "include" });
      if (!response.ok) throw new Error("pairing_failed");
      setCode((await response.json() as { code: string }).code);
    } finally { setBusy(false); }
  };
  return <main className="basket-pairing"><img src={brand.mark} alt="Basket" /><p>CHROME EXTENSION</p><h1>BasketをGoogle Meetへ接続</h1><span>{displayName}さんとして接続します。コードは一度だけ使え、10分で失効します。</span>{code ? <button type="button" className="basket-pairing-code" onClick={() => void navigator.clipboard.writeText(code)}>{code}</button> : <button type="button" disabled={busy} onClick={() => void issue()}>{busy ? "発行しています…" : "接続コードを発行"}</button>}<a href="/">Basketへ戻る</a></main>;
}

function useAmbientBgm(enabled: boolean, volume: number) {
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const gainRef = useRef<GainNode | undefined>(undefined);
  useEffect(() => {
    const audio = new Audio(brand.bgm); audio.loop = true; audio.preload = "auto"; audio.volume = 1; audioRef.current = audio;
    const context = new AudioContext({ latencyHint: "interactive" });
    const source = context.createMediaElementSource(audio);
    const gain = context.createGain();
    source.connect(gain).connect(context.destination);
    audioContextRef.current = context;
    gainRef.current = gain;
    return () => {
      audio.pause(); source.disconnect(); gain.disconnect(); void context.close();
      audio.removeAttribute("src"); audio.load(); audioRef.current = undefined; audioContextRef.current = undefined; gainRef.current = undefined;
    };
  }, []);
  const start = useCallback(async () => {
    const audio = audioRef.current; const context = audioContextRef.current;
    if (!audio || !context || !canPlayAmbientBgm(currentAmbientBgmPageState())) return false;
    try {
      await context.resume();
      await audio.play();
      return true;
    } catch {
      return false;
    }
  }, []);
  useEffect(() => {
    const audio = audioRef.current; const context = audioContextRef.current; const gain = gainRef.current;
    if (!audio || !context || !gain) return;
    gain.gain.setTargetAtTime(Math.max(0, Math.min(.2, volume)), context.currentTime, .03);
    const pause = (reset = false) => {
      audio.pause();
      if (reset) audio.currentTime = 0;
      void context.suspend().catch(() => undefined);
    };
    const syncWithPage = () => {
      if (!enabled || !canPlayAmbientBgm(currentAmbientBgmPageState())) {
        pause(!enabled);
        return;
      }
      void start();
    };
    const handlePageHide = () => pause(false);
    if (enabled) {
      syncWithPage();
      window.addEventListener("pointerdown", syncWithPage, { passive: true });
      window.addEventListener("touchstart", syncWithPage, { passive: true });
      window.addEventListener("keydown", syncWithPage);
    } else {
      pause(true);
    }
    document.addEventListener("visibilitychange", syncWithPage);
    window.addEventListener("focus", syncWithPage);
    window.addEventListener("blur", syncWithPage);
    window.addEventListener("pageshow", syncWithPage);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pointerdown", syncWithPage);
      window.removeEventListener("touchstart", syncWithPage);
      window.removeEventListener("keydown", syncWithPage);
      document.removeEventListener("visibilitychange", syncWithPage);
      window.removeEventListener("focus", syncWithPage);
      window.removeEventListener("blur", syncWithPage);
      window.removeEventListener("pageshow", syncWithPage);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [enabled, start, volume]);
  return { start };
}

export function AppV2() {
  const [bootstrap, setBootstrap] = useState<BasketBootstrap | null | undefined>();
  const [selectedAgent, setSelectedAgent] = useState<AgentId | null>(null);
  const [thread, setThread] = useState<ThreadRecord>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [observerActivities, setObserverActivities] = useState<ObserverWorkActivity[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>("idle");
  const [connectionError, setConnectionError] = useState<string>();
  const [active, setActive] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [mode, setMode] = useState<SessionMode>("conversation");
  const [idleWarning, setIdleWarning] = useState(false);
  const [usageSummary, setUsageSummary] = useState<Record<string, unknown>>();
  const [bgmPreview, setBgmPreview] = useState<{ enabled: boolean; volume: number }>();
  const [quickBgmEnabled, setQuickBgmEnabled] = useState<boolean | undefined>(() => readPendingBgmPreference());
  const [localBgm, setLocalBgm] = useState<LocalBgmPreference>(() => readLocalBgmPreference());
  const realtime = useRef<BasketRealtimeClientType | undefined>(undefined);
  const selectedAgentRef = useRef<AgentId | null>(null);
  const selectionVersion = useRef(0);
  const openingAgent = useRef<AgentId | undefined>(undefined);
  const pendingWake = useRef<"double_click" | "long_press" | undefined>(undefined);
  const pendingBgmSync = useRef(false);
  const idle = useRef<IdleLifecycle | undefined>(undefined);
  const threadRef = useRef<ThreadRecord | undefined>(undefined);
  threadRef.current = thread;
  selectedAgentRef.current = selectedAgent;
  const configuredBgmEnabled = bgmPreview?.enabled ?? bootstrap?.settings.bgmEnabled ?? localBgm.enabled;
  const bgmEnabled = quickBgmEnabled ?? configuredBgmEnabled;
  const bgmVolume = bgmPreview?.volume ?? bootstrap?.settings.bgmVolume ?? localBgm.volume;
  const ambientBgm = useAmbientBgm(Boolean(bgmEnabled && !active), bgmVolume);

  const refreshHistory = useCallback(async () => { const result = await api.threads(50); setHistory(historyItems(result.threads)); }, []);
  const refreshUsage = useCallback(async () => { const result = await api.usage("month"); setUsageSummary(result.summary); }, []);
  useEffect(() => {
    void api.bootstrap().then((value) => { setBootstrap(value); return Promise.all([api.threads(50), api.usage("month")]); })
      .then(([threads, usage]) => { setHistory(historyItems(threads.threads)); setUsageSummary(usage.summary); })
      .catch((error) => setBootstrap(error instanceof BasketApiError && error.status === 401 ? null : null));
    return () => { idle.current?.stop(); realtime.current?.close("page_unload"); };
  }, []);
  useEffect(() => {
    if (!bootstrap) return;
    const pending = readPendingBgmPreference();
    const value = { enabled: pending ?? bootstrap.settings.bgmEnabled, volume: bootstrap.settings.bgmVolume };
    setLocalBgm(value);
    storeLocalBgmPreference(value);
    if (pending === undefined) return;
    setQuickBgmEnabled(pending);
    if (pendingBgmSync.current) return;
    pendingBgmSync.current = true;
    void updateBgmEnabled(bootstrap.settings, pending).then((result) => {
      clearPendingBgmPreference();
      pendingBgmSync.current = false;
      setQuickBgmEnabled(undefined);
      setBootstrap((current) => current ? { ...current, settings: result.settings } : current);
      const synced = { enabled: result.settings.bgmEnabled, volume: result.settings.bgmVolume };
      setLocalBgm(synced);
      storeLocalBgmPreference(synced);
    }).catch(() => { pendingBgmSync.current = false; });
  }, [bootstrap]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void refreshUsage(), 10_000);
    return () => window.clearInterval(timer);
  }, [active, refreshUsage]);

  const appendTurn = useCallback((role: ChatMessage["role"], text: string, source: "speech_transcript" | "meeting_transcript" | "typed" | "assistant_final" | "tool_result" | "system") => {
    const currentThread = threadRef.current; const trimmed = text.trim(); if (!currentThread || !trimmed) return;
    const clientEventId = crypto.randomUUID();
    setMessages((current) => [...current, chatMessage(role, trimmed, clientEventId)]);
    void api.appendTurn(currentThread.id, { clientEventId, role: role === "system" ? "system" : role, text: trimmed, source, surface: "web", at: new Date().toISOString() })
      .then(({ thread: updated }) => { setThread(updated); void refreshHistory(); }).catch(() => undefined);
  }, [refreshHistory]);

  const stop = useCallback((reason: RealtimeEndReason = "user", note?: string) => {
    idle.current?.stop(); realtime.current?.close(reason); realtime.current = undefined; setObserverActivities([]); setActive(false); setConnectionState("idle"); setIdleWarning(false);
    if (note) appendTurn("system", note, "system");
  }, [appendTurn]);

  const returnToTop = useCallback(() => {
    selectionVersion.current += 1;
    stop("agent_switch");
    selectedAgentRef.current = null;
    threadRef.current = undefined;
    setSelectedAgent(null);
    setThread(undefined);
    setMessages([]);
    setConnectionError(undefined);
  }, [stop]);

  const connect = useCallback(async (agent: AgentId, target: ThreadRecord, wake?: "double_click" | "long_press", inputStream?: MediaStream) => {
    stop("agent_switch"); setSelectedAgent(agent); setThread(target); threadRef.current = target; setActive(true); setConnectionState("connecting"); setConnectionError(undefined);
    idle.current = new IdleLifecycle((state) => { if (state === "warning") setIdleWarning(true); if (state === "ended") stop("idle_timeout", "無音が続いたため通話を終了しました"); });
    idle.current.start();
    const { BasketRealtimeClient } = await import("@basket/realtime");
    const client = new BasketRealtimeClient({ apiBaseUrl: "", agent, mode, surface: "web", threadId: target.id, ...(inputStream ? { inputStream, ownsInputStream: true } : {}), onState: setConnectionState,
      onMeaningfulActivity: () => { setIdleWarning(false); idle.current?.activity(); },
      onUsageChanged: () => void refreshUsage(),
      onObserverActivities: setObserverActivities,
      onUserTranscript: (text) => appendTurn("user", text, "speech_transcript"), onAssistantTranscript: (text) => appendTurn("assistant", text, "assistant_final"),
      onToolEvent: (event) => appendTurn("system", event.message, "system") });
    realtime.current = client;
    try { await client.connect(); client.setMicMuted(micMuted); client.setSpeakerMuted(speakerMuted); if (wake) client.wake(wake); }
    catch (error) {
      const message = connectionFailureMessage(error);
      console.error("[basket] realtime connection failed", error);
      stop("error"); appendTurn("system", message, "system"); setConnectionError(message); setConnectionState("error");
    }
  }, [appendTurn, micMuted, mode, refreshUsage, speakerMuted, stop]);

  const openAgent = useCallback(async (agent: AgentId, wake?: "double_click" | "long_press", inputStreamPromise?: Promise<MediaStream>) => {
    if (openingAgent.current === agent) {
      if (wake) pendingWake.current = wake;
      void inputStreamPromise?.then((stream) => stream.getTracks().forEach((track) => track.stop())).catch(() => undefined);
      return;
    }
    const version = ++selectionVersion.current;
    openingAgent.current = agent;
    selectedAgentRef.current = agent;
    setSelectedAgent(agent); setConnectionState("connecting"); setConnectionError(undefined);
    let inputStream: MediaStream | undefined;
    try {
      inputStream = await inputStreamPromise;
      const opened = await api.openThread(agent, "web"); const detail = await api.thread(opened.thread.id);
      if (version !== selectionVersion.current || selectedAgentRef.current !== agent) {
        inputStream?.getTracks().forEach((track) => track.stop());
        inputStream = undefined;
        return;
      }
      setThread(detail.thread); threadRef.current = detail.thread;
      setMessages(detail.turns.map((turn) => chatMessage(turn.role === "tool" || turn.role === "system" ? "system" : turn.role, turn.text, turn.id)));
      await connect(agent, detail.thread, wake, inputStream);
      inputStream = undefined;
      if (pendingWake.current) { realtime.current?.wake(pendingWake.current); pendingWake.current = undefined; }
    } catch (error) {
      inputStream?.getTracks().forEach((track) => track.stop());
      const message = connectionFailureMessage(error);
      console.error("[basket] agent open failed", error);
      setActive(false); setConnectionError(message); setConnectionState("error");
    } finally { if (openingAgent.current === agent) openingAgent.current = undefined; }
  }, [connect]);

  const requestMicrophone = () => navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
  const reconnect = (agent: AgentId, target: ThreadRecord) => {
    void requestMicrophone().then((stream) => connect(agent, target, undefined, stream)).catch((error) => {
      setConnectionError(connectionFailureMessage(error));
      setConnectionState("error");
    });
  };

  const selectAgent = (agent: AgentId) => {
    if (selectedAgentRef.current === agent) return;
    const version = ++selectionVersion.current;
    stop("agent_switch");
    selectedAgentRef.current = agent;
    threadRef.current = undefined;
    setSelectedAgent(agent);
    setThread(undefined);
    setMessages([]);
    setConnectionError(undefined);
    setConnectionState("idle");
    void api.openThread(agent, "web").then((opened) => api.thread(opened.thread.id)).then((detail) => {
      if (version !== selectionVersion.current || selectedAgentRef.current !== agent) return;
      setThread(detail.thread);
      threadRef.current = detail.thread;
      setMessages(detail.turns.map((turn) => chatMessage(turn.role === "tool" || turn.role === "system" ? "system" : turn.role, turn.text, turn.id)));
    }).catch((error) => {
      if (version !== selectionVersion.current || selectedAgentRef.current !== agent) return;
      console.error("[basket] agent selection failed", error);
      setConnectionError("会話履歴を読み込めませんでした。少し待ってからもう一度お試しください。");
      setConnectionState("error");
    });
  };

  const applyAudioMode = (next: AudioMode) => {
    const nextMode: SessionMode = next === "conversation" ? "conversation" : "observer"; const muted = next === "muted";
    setMode(nextMode); setSpeakerMuted(muted); realtime.current?.setMode(nextMode); realtime.current?.setSpeakerMuted(muted); idle.current?.activity();
  };
  const saveSettings = async (value: UserSettings) => {
    const { version, timezone, profile, voiceByAgent, bgmEnabled, bgmVolume } = value;
    const result = await api.updateSettings({ version, timezone, profile, voiceByAgent, bgmEnabled, bgmVolume });
    setBootstrap((current) => current ? { ...current, settings: result.settings } : current);
    const local = { enabled: result.settings.bgmEnabled, volume: result.settings.bgmVolume };
    setLocalBgm(local); storeLocalBgmPreference(local);
    clearPendingBgmPreference();
    setQuickBgmEnabled(undefined);
    setBgmPreview(undefined);
  };
  const toggleBgm = () => {
    const next = !bgmEnabled;
    const local = { enabled: next, volume: bgmVolume };
    setQuickBgmEnabled(next);
    setLocalBgm(local);
    storeLocalBgmPreference(local);
    storePendingBgmPreference(next);
    if (next) void ambientBgm.start();
    if (!bootstrap) return;
    pendingBgmSync.current = true;
    void updateBgmEnabled(bootstrap.settings, next).then((result) => {
      clearPendingBgmPreference();
      pendingBgmSync.current = false;
      setQuickBgmEnabled(undefined);
      setBootstrap((current) => current ? { ...current, settings: result.settings } : current);
      const synced = { enabled: result.settings.bgmEnabled, volume: result.settings.bgmVolume };
      setLocalBgm(synced);
      storeLocalBgmPreference(synced);
    }).catch(() => { pendingBgmSync.current = false; });
  };
  const managedProfile = useMemo(() => bootstrap ? { displayName: bootstrap.member.displayName, email: bootstrap.member.email } : undefined, [bootstrap]);
  if (bootstrap === undefined) return <BasketPage bgmEnabled={bgmEnabled} onToggleBgm={toggleBgm}><Loading /></BasketPage>;
  if (bootstrap === null) return <BasketPage bgmEnabled={bgmEnabled} onToggleBgm={toggleBgm}><Login /></BasketPage>;
  if (window.location.pathname === "/extension") return <BasketPage bgmEnabled={bgmEnabled} onToggleBgm={toggleBgm}><ExtensionPairing displayName={bootstrap.member.displayName} /></BasketPage>;

  return <BasketPage bgmEnabled={bgmEnabled} onToggleBgm={toggleBgm}><BasketShell agents={bootstrap.member.agents} assets={assets} brandMark={brand.mark} brandLogo={brand.logo} selectedAgent={selectedAgent}
    connectionState={connectionState} {...(connectionError ? { errorMessage: connectionError } : idleWarning ? { errorMessage: "まもなく通話を終了します。話すか画面を操作すると継続します" } : {})}
    mode={mode} active={active} micMuted={micMuted} speakerMuted={speakerMuted} messages={messages} observerActivities={observerActivities} history={history} threadOpen={thread?.status === "open"}
    settings={bootstrap.settings} {...(managedProfile ? { managedProfile } : {})} {...(usageSummary ? { usageSummary } : {})}
    extensionDownloadUrl="/downloads/basket-meet-extension.zip"
    onSelectAgent={selectAgent} onSelectTop={returnToTop} onWakeAgent={(agent, source) => { if (active && selectedAgentRef.current === agent) realtime.current?.wake(source); else void openAgent(agent, source, requestMicrophone()); }}
    onToggleSession={() => { if (active) stop("user"); else if (selectedAgent && thread?.status === "open") reconnect(selectedAgent, thread); else if (selectedAgent) void openAgent(selectedAgent, undefined, requestMicrophone()); }}
    onToggleMic={() => setMicMuted((current) => { realtime.current?.setMicMuted(!current); idle.current?.activity(); return !current; })}
    onCycleAudioMode={() => applyAudioMode(nextAudioMode(audioModeFor(mode, speakerMuted)))}
    onSendMessage={(text) => { if (!selectedAgent) return; const inputStreamPromise = !active || !threadRef.current ? requestMicrophone() : undefined; const send = async () => { if (!active || !threadRef.current) await openAgent(selectedAgent, undefined, inputStreamPromise); appendTurn("user", text, "typed"); realtime.current?.sendText(text); }; void send(); }}
    onCloseThread={() => { const id = threadRef.current?.id; if (!id) return; returnToTop(); void api.closeThread(id).then(() => refreshHistory()); }}
    onSelectHistory={(id) => { const version = ++selectionVersion.current; void api.thread(id).then((detail) => { if (version !== selectionVersion.current) return; stop("agent_switch"); selectedAgentRef.current = detail.thread.agent; setSelectedAgent(detail.thread.agent); setThread(detail.thread); threadRef.current = detail.thread; setMessages(detail.turns.map((turn) => chatMessage(turn.role === "tool" || turn.role === "system" ? "system" : turn.role, turn.text, turn.id))); }); }}
    onOpenSettings={() => void refreshUsage()} onPreviewBgm={(enabled, volume) => setBgmPreview({ enabled, volume })} onSaveSettings={saveSettings} onLogout={() => void api.logout().finally(() => window.location.reload())} onLogoutAll={() => void api.logoutAll().finally(() => window.location.reload())} />
  </BasketPage>;
}
